import { useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, ListTable, Notice, TabPanel, Tabs, type ListColumn } from "@wilco/ui";
import { t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import type { AccessDay, AccessPass, Agent } from "./api.ts";
import { BackToHr, DayField, useNames } from "./common.tsx";
import { accessDaysQuery, agentsQuery, passesQuery } from "./queries.ts";
import { localDate, localTime } from "./time.ts";

export type DoorsTab = "day" | "log" | "panels";

const route = getRouteApi("/app/hr/doors");

/**
 * «Двері» (ACC, ДВ-01..07): проходы через панели TVT у дверей. День
 * собирается по «Картка №» (номер человека в списке панели, не бейдж EM):
 * номер без сотрудника это «Невідома картка», его вписывают в карточке.
 */
export function DoorsPage() {
  const { tab = "day", date } = route.useSearch();
  const navigate = useNavigate();
  const day = date ?? localDate();
  const go = (s: { tab?: DoorsTab; date?: string }) => void navigate({ to: "/hr/doors", search: { tab, date, ...s }, replace: true });
  return (
    <div className="page people">
      <div className="page-head">
        <BackToHr />
        <h1 className="page-title">{t("doors.title")}</h1>
      </div>
      <Tabs
        label={t("doors.title")}
        value={tab}
        onValueChange={(v) => go({ tab: v })}
        items={[
          { value: "day", label: t("doors.tab.day") },
          { value: "log", label: t("doors.tab.log") },
          { value: "panels", label: t("doors.tab.panels") },
        ]}
      >
        <TabPanel value="day">
          <div className="people-tab">
            <DayField value={day} onChange={(d) => go({ date: d })} />
            <DayTab day={day} />
          </div>
        </TabPanel>
        <TabPanel value="log">
          <div className="people-tab">
            <DayField value={day} onChange={(d) => go({ date: d })} />
            <LogTab day={day} />
          </div>
        </TabPanel>
        <TabPanel value="panels">
          <PanelsTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

const time = (iso: string | null | undefined) => (iso ? localTime(iso) : "—");

function DayTab({ day }: { day: string }) {
  const days = useQuery(accessDaysQuery(day));
  const columns: ListColumn<AccessDay>[] = [
    { key: "who", header: t("doors.employee"), kind: "title", render: (d) => d.employeeName ?? <span className="people-unknown">{t("doors.unknownCard")}</span> },
    { key: "card", header: t("people.cardNo"), kind: "text", width: "6rem", render: (d) => <span className="w-num">{d.cardNo}</span> },
    { key: "entries", header: t("doors.entries"), kind: "text", render: (d) => d.entries || "—" },
    { key: "arrived", header: t("doors.arrived"), kind: "number", width: "6rem", render: (d) => time(d.arrived) },
    {
      key: "late",
      header: t("doors.late"),
      kind: "number",
      width: "7rem",
      render: (d) => (d.lateMinutes > 0 ? <Badge tone="warning">{t("doors.lateMin", { n: d.lateMinutes })}</Badge> : "—"),
    },
    { key: "reentries", header: t("doors.reentries"), kind: "number", width: "7rem", render: (d) => String(d.reentries) },
    { key: "denied", header: t("doors.denied"), kind: "number", width: "6rem", render: (d) => (d.denied > 0 ? <Badge tone="danger">{String(d.denied)}</Badge> : "0") },
  ];
  return (
    <>
      {days.error ? <Notice tone="danger" title={errorNotice(days.error).title} className="stock-notice" /> : null}
      <ListTable label={t("doors.tab.day")} columns={columns} rows={days.data ?? []} rowKey={(d) => d.cardNo} empty={days.data ? t("doors.empty") : null} />
    </>
  );
}

function LogTab({ day }: { day: string }) {
  const passes = useQuery(passesQuery(day));
  const name = useNames();
  const columns: ListColumn<AccessPass>[] = [
    { key: "who", header: t("doors.employee"), kind: "title", render: (p) => (p.employeeId ? name(p.employeeId) : <span className="people-unknown">{t("doors.unknownCard")}</span>) },
    { key: "time", header: t("doors.time"), kind: "text", width: "5rem", render: (p) => <span className="w-num">{localTime(p.at)}</span> },
    { key: "panel", header: t("doors.panel"), kind: "text", width: "10rem", render: (p) => p.panel },
    { key: "card", header: t("people.cardNo"), kind: "text", width: "6rem", render: (p) => <span className="w-num">{p.cardNo ?? "—"}</span> },
    {
      key: "result",
      header: t("doors.result"),
      kind: "text",
      width: "9rem",
      render: (p) => (
        <span className="tags">
          {p.granted ? <Badge tone="success">{t("doors.granted")}</Badge> : <Badge tone="danger">{t("doors.deniedOne")}</Badge>}
          {p.repeat ? <Badge>{t("doors.repeat")}</Badge> : null}
        </span>
      ),
    },
    { key: "since", header: t("doors.since"), kind: "number", width: "7rem", render: (p) => (p.sincePreviousMinutes > 0 ? String(p.sincePreviousMinutes) : "—") },
  ];
  return (
    <>
      {passes.error ? <Notice tone="danger" title={errorNotice(passes.error).title} className="stock-notice" /> : null}
      <ListTable label={t("doors.tab.log")} columns={columns} rows={passes.data ?? []} rowKey={(p) => p.id} empty={passes.data ? t("doors.emptyLog") : null} />
    </>
  );
}

type PanelRow = Agent["panels"][number] & { agent: string; online: boolean };

/** Панели у дверей: их опрашивает агент в сети офиса, сервер видит последний отчёт. */
function PanelsTab() {
  const agents = useQuery(agentsQuery);
  const rows: PanelRow[] = (agents.data ?? []).filter((a) => !a.revokedAt).flatMap((a) => a.panels.map((p) => ({ ...p, agent: a.name, online: a.online })));
  const columns: ListColumn<PanelRow>[] = [
    { key: "name", header: t("doors.panel"), kind: "title", render: (p) => p.name },
    { key: "host", header: t("doors.host"), kind: "text", width: "9rem", render: (p) => <span className="w-num">{p.host}</span> },
    { key: "agent", header: t("doors.agent"), kind: "text", width: "10rem", render: (p) => p.agent },
    {
      key: "state",
      header: t("doors.state"),
      kind: "text",
      width: "11rem",
      render: (p) =>
        p.loginStopped ? (
          <Badge tone="danger">{t("doors.loginStopped")}</Badge>
        ) : !p.online ? (
          <Badge tone="warning">{t("doors.agentOffline")}</Badge>
        ) : p.lastErrorCode ? (
          <Badge tone="warning">{t("doors.error", { code: p.lastErrorCode })}</Badge>
        ) : (
          <Badge tone="success">{t("doors.ok")}</Badge>
        ),
    },
    {
      key: "report",
      header: t("doors.lastReport"),
      kind: "number",
      width: "9rem",
      render: (p) => (p.lastReportAt ? `${localDate(Date.parse(p.lastReportAt)).slice(5).split("-").reverse().join(".")} ${localTime(p.lastReportAt)}` : "—"),
    },
  ];
  return (
    <div className="people-tab">
      {agents.error ? <Notice tone="danger" title={errorNotice(agents.error).title} className="stock-notice" /> : null}
      <p className="w-muted people-caption">{t("doors.panelsHint")}</p>
      <ListTable label={t("doors.tab.panels")} columns={columns} rows={rows} rowKey={(p) => p.id} empty={agents.data ? t("doors.noPanels") : null} />
    </div>
  );
}
